import { useCallback, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { ColorButton } from "./ColorButton";
import { Toolbar } from "../../ui/Toolbar";
import { Icon } from "../../ui/Icon";
import { EDITOR_MARK_SPECIAL_COLORS } from "../../../utils/constants";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { useThemeContextSelector } from "@/utils/theme";
import Box from "@mui/material/Box";

export type ColorPickerProps = {
    color?: string;
    onChange?: (color: string) => void;
    onClear?: () => void;
};

export const ColorPicker = ({ color, onChange, onClear }: ColorPickerProps) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const [colorInputValue, setColorInputValue] = useState(color || "");

    const handleColorUpdate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setColorInputValue(event.target.value);
    }, []);

    const handleColorChange = useCallback(() => {
        const isCorrectColor = /^#([0-9A-F]{3}){1,2}$/i.test(colorInputValue);

        if (!isCorrectColor) {
            if (onChange) {
                onChange("");
            }

            return;
        }

        if (onChange) {
            onChange(colorInputValue);
        }
    }, [colorInputValue, onChange]);

    return (
        <FlexColumn style={{ gap: "8px" }}>
            <HexColorPicker style={{ width: "100%" }} color={color || ""} onChange={onChange} />
            <Box
                component="input"
                type="text"
                placeholder="#000000"
                value={colorInputValue}
                onChange={handleColorUpdate}
                onBlur={handleColorChange}
                onKeyDown={e => {
                    if (e.key === "Enter") {
                        handleColorChange();
                    }
                }}
                sx={{
                    width: "100%",
                    padding: "8px",
                    color: isDarkMode ? "#FFFFFF" : "#000000",
                    backgroundColor: isDarkMode ? "#000000" : "#FFFFFF",
                    border: "1px solid",
                    borderColor: isDarkMode ? "#1A1A1A" : "#E6E6E6",
                    fontSize: "16px",
                    fontFamily: "var(--knowt-font)",
                    borderRadius: "4px",
                    "&:focus": {
                        outline: "1px solid",
                        outlineColor: isDarkMode ? "#404040" : "#D4D4D4",
                        ring: "0",
                    },
                }}
            />
            <FlexRowAlignCenter style={{ flexWrap: "wrap", gap: "4px", maxWidth: "240px" }}>
                {EDITOR_MARK_SPECIAL_COLORS.map(currentColor => (
                    <ColorButton
                        active={currentColor === color}
                        color={currentColor}
                        key={currentColor}
                        onColorChange={onChange}
                    />
                ))}
                <Toolbar.Button tooltip="Reset color to default" onClick={onClear}>
                    <Icon name="Undo" />
                </Toolbar.Button>
            </FlexRowAlignCenter>
        </FlexColumn>
    );
};
